import { useEffect, useRef, useState, type FormEvent } from "react";
import { z } from "zod";
import { CheckCircle2, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useI18n } from "@/i18n";
import { localizedPath } from "@/i18n/route-manifest";
import { business } from "@/config/business";
import {
  captureAttribution,
  isLeadTransportConfigured,
  LeadTransportUnavailableError,
  submitLead,
} from "@/lib/leads";
import { initializeAnalytics, trackAnalyticsEvent } from "@/lib/analytics";
import { cn } from "@/lib/utils";

export function QuoteForm() {
  const { t, locale } = useI18n();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error" | "unavailable">(
    isLeadTransportConfigured() ? "idle" : "unavailable",
  );
  const startTrackedRef = useRef(false);
  const successRef = useRef<HTMLDivElement>(null);

  // Capture Google Ads / UTM attribution on mount so it survives navigation.
  useEffect(() => {
    initializeAnalytics();
    captureAttribution();
  }, []);

  useEffect(() => {
    if (status === "sent") successRef.current?.focus();
  }, [status]);

  function trackStart() {
    if (startTrackedRef.current) return;
    startTrackedRef.current = true;
    trackAnalyticsEvent({ event: "quote_form_start", form_id: "roof_assessment" });
  }

  const schema = z.object({
    name: z.string().trim().min(1, t.form.errorRequired).max(100),
    phone: z
      .string()
      .trim()
      .min(7, t.form.errorPhone)
      .max(30)
      .regex(/^[0-9+()\-.\s]+$/, t.form.errorPhone),
    email: z.string().trim().email(t.form.errorEmail).max(255),
    postalCode: z.string().trim().min(3, t.form.errorRequired).max(12),
    roofingNeed: z.string().min(1, t.form.errorRequired),
    timeline: z.string().min(1, t.form.errorRequired),
    message: z.string().trim().max(1500).optional().default(""),
  });

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = Object.fromEntries(new FormData(form).entries());
    const parsed = schema.safeParse(data);

    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0]);
        if (!next[key]) next[key] = issue.message;
      }
      setErrors(next);
      const first = form.querySelector<HTMLElement>(`[name="${Object.keys(next)[0]}"]`);
      first?.focus();
      return;
    }

    setErrors({});
    setStatus("sending");
    try {
      await submitLead({ ...parsed.data, locale });
      trackAnalyticsEvent({ event: "quote_form_submit", form_id: "roof_assessment" });
      form.reset();
      window.location.assign(localizedPath("core:thank-you", locale));
    } catch (error) {
      setStatus(error instanceof LeadTransportUnavailableError ? "unavailable" : "error");
    }
  }

  if (status === "sent") {
    return (
      <div
        ref={successRef}
        tabIndex={-1}
        role="status"
        aria-live="polite"
        className="rounded-lg border border-border bg-card p-8 text-center shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <CheckCircle2 className="mx-auto size-7 text-accent" aria-hidden="true" />
        <h3 className="display-3 mt-4 text-foreground">{t.form.successTitle}</h3>
        <p className="mt-2 text-sm text-muted-foreground">{t.form.successBody}</p>
        <Button variant="quiet" size="xl" asChild className="mt-6">
          <a
            href={business.phoneHref}
            className="gap-2"
            onClick={() =>
              trackAnalyticsEvent({ event: "phone_click", link_location: "quote_success" })
            }
          >
            <Phone aria-hidden="true" />
            {business.phone}
          </a>
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      onFocusCapture={trackStart}
      onChange={trackStart}
      noValidate
      aria-describedby={
        status === "error" || status === "unavailable" ? "quote-form-status" : undefined
      }
      className="rounded-lg border border-border bg-card p-6 shadow-card sm:p-8"
    >
      <span className="sr-only" role="status" aria-live="polite">
        {status === "sending" ? t.form.submitting : ""}
      </span>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="name" label={t.form.name} error={errors["name"]}>
          <Input
            id="name"
            name="name"
            autoComplete="name"
            required
            aria-invalid={!!errors["name"]}
            aria-describedby={errors["name"] ? "name-error" : undefined}
          />
        </Field>
        <Field id="phone" label={t.form.phone} error={errors["phone"]}>
          <Input
            id="phone"
            name="phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            required
            aria-invalid={!!errors["phone"]}
            aria-describedby={errors["phone"] ? "phone-error" : undefined}
          />
        </Field>
        <Field id="email" label={t.form.email} error={errors["email"]}>
          <Input
            id="email"
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            aria-invalid={!!errors["email"]}
            aria-describedby={errors["email"] ? "email-error" : undefined}
          />
        </Field>
        <Field id="postalCode" label={t.form.postal} error={errors["postalCode"]}>
          <Input
            id="postalCode"
            name="postalCode"
            autoComplete="postal-code"
            required
            aria-invalid={!!errors["postalCode"]}
            aria-describedby={errors["postalCode"] ? "postalCode-error" : undefined}
          />
        </Field>
        <Field id="roofingNeed" label={t.form.need} error={errors["roofingNeed"]}>
          <NativeSelect
            id="roofingNeed"
            name="roofingNeed"
            options={t.form.needOptions}
            error={errors["roofingNeed"]}
          />
        </Field>
        <Field id="timeline" label={t.form.timeline} error={errors["timeline"]}>
          <NativeSelect
            id="timeline"
            name="timeline"
            options={t.form.timelineOptions}
            error={errors["timeline"]}
          />
        </Field>
        <Field
          id="message"
          label={t.form.message}
          error={errors["message"]}
          className="sm:col-span-2"
        >
          <Textarea id="message" name="message" rows={4} />
        </Field>
      </div>

      {status === "error" || status === "unavailable" ? (
        <p id="quote-form-status" role="alert" className="mt-4 text-sm text-destructive">
          {status === "unavailable" ? t.form.errorUnavailable : t.form.errorSubmit}
        </p>
      ) : null}

      <Button
        type="submit"
        variant="brand"
        size="xl"
        className="mt-6 w-full"
        disabled={status === "sending" || status === "unavailable"}
      >
        {status === "sending" ? t.form.submitting : t.form.submit}
      </Button>
      <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
        {t.form.privacy}{" "}
        <a
          href={localizedPath("core:privacy", locale)}
          className="underline underline-offset-2 hover:text-foreground"
        >
          {t.form.privacyLink}
        </a>
      </p>
    </form>
  );
}

function Field({
  id,
  label,
  error,
  className,
  children,
}: {
  id: string;
  label: string;
  error?: string | undefined;
  className?: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <div className={cn("min-w-0", className)}>
      <Label htmlFor={id} className="text-[0.8rem] font-medium text-foreground">
        {label}
      </Label>
      <div className="mt-1.5">{children}</div>
      {error ? (
        <p id={`${id}-error`} role="alert" className="mt-1.5 text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function NativeSelect({
  id,
  name,
  options,
  error,
}: {
  id: string;
  name: string;
  options: readonly string[];
  error?: string | undefined;
}) {
  return (
    <select
      id={id}
      name={name}
      defaultValue=""
      required
      aria-invalid={!!error}
      aria-describedby={error ? `${id}-error` : undefined}
      className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground shadow-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none"
    >
      <option value="" disabled>
        -
      </option>
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
}
